import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  TaskType,
  TaskStatus,
  TaskUsage,
  TaskHistory,
  TaskHistoryItem,
  TaskStats,
  TaskAvailability,
  TaskLimit,
  getTaskUsage_api,
  getTaskHistory_api,
  getTaskStats_api,
  checkTaskAvailability_api,
  getAllTaskLimits_api
} from 'api/maestroTasksApi';
import { getRecommendedTasks_api } from 'api/agentApi';
import { AgentTaskOptions } from "api/maestroTasksApi";

// Re-export enums for easy access
export { TaskType, TaskStatus };
import Logger from 'Logger';
import useBrand from 'Hooks/useBrand'; // Import useBrand

export interface MaestroTasksState {
  /* States for task usage */
  taskUsage: TaskUsage | null;
  isLoadingUsage: boolean;
  hasUsageError: boolean;

  /* States for task history */
  taskHistory: TaskHistoryItem[];
  totalHistoryCount: number;
  isLoadingHistory: boolean;
  hasHistoryError: boolean;

  /* States for task stats */
  taskStats: TaskStats | null;
  isLoadingStats: boolean;
  hasStatsError: boolean;

  /* States for task limits (admin) */
  taskLimits: TaskLimit[];
  isLoadingLimits: boolean;
  hasLimitsError: boolean;

  /* States for task pack modal */
  taskPackModalOpen: boolean;

  /* States for recommended tasks */
  recommendedTasks: AgentTaskOptions[];
  isLoadingRecommendedTasks: boolean;
  hasRecommendedTasksError: boolean;

  /* Computed getters */
  getTasksRemaining: () => number;
  getCanConsumeTask: () => boolean;
  getNeedsUpgrade: () => boolean;
  getUsagePercentage: () => number;

  /* Actions */
  setTaskUsage: (usage: TaskUsage) => void;
  setTaskHistory: (history: TaskHistoryItem[], total: number) => void;
  setTaskStats: (stats: TaskStats) => void;
  resetTaskData: () => void;

  /* Methods */
  setTaskPackModalOpen: (open: boolean) => void;
  fetchTaskUsage: (brandId: number) => Promise<void>;
  fetchTaskHistory: (brandId: number, options?: {
    limit?: number;
    offset?: number;
    taskType?: TaskType;
    status?: TaskStatus;
  }) => Promise<void>;
  fetchTaskStats: (brandId: number) => Promise<void>;
  checkTaskAvailability: (brandId: number, taskType: TaskType) => Promise<TaskAvailability>;
  fetchAllTaskLimits: () => Promise<void>;
  fetchRecommendedTasks: (brandId: number) => Promise<void>;
  refreshTaskData: (brandId: number) => Promise<void>;
}

const useMaestroTasks = create(immer<MaestroTasksState>((set, get) => ({
  /* Initial state */
  taskUsage: null,
  isLoadingUsage: false,
  hasUsageError: false,
  taskPackModalOpen: false,

  taskHistory: [],
  totalHistoryCount: 0,
  isLoadingHistory: false,
  hasHistoryError: false,

  taskStats: null,
  isLoadingStats: false,
  hasStatsError: false,

  taskLimits: [],
  isLoadingLimits: false,
  hasLimitsError: false,

  recommendedTasks: [],
  isLoadingRecommendedTasks: false,
  hasRecommendedTasksError: false,

  /* Computed getters */
  getTasksRemaining: () => {
    const { taskUsage } = get();
    return taskUsage?.remaining || 0;
  },

  getCanConsumeTask: () => {
    const { taskUsage } = get();
    if (!taskUsage) {
      return false;
    }
    return taskUsage.remaining > 0 || taskUsage.extraTasksAvailable > 0;
  },

  getNeedsUpgrade: () => {
    const { taskUsage } = get();
    if (!taskUsage) {
      return false;
    }
    return taskUsage.remaining <= 0 && taskUsage.extraTasksAvailable <= 0;
  },

  getUsagePercentage: () => {
    const { taskUsage } = get();
    if (!taskUsage || taskUsage.monthlyLimit === 0) {
      return 0;
    }
    return Math.round((taskUsage.used / taskUsage.monthlyLimit) * 100);
  },

  /* Actions */
  setTaskUsage: (usage: TaskUsage) => {
    set(state => {
      state.taskUsage = usage;
    });
  },

  setTaskPackModalOpen: (open: boolean) => {
    set(state => {
      state.taskPackModalOpen = open;
    });
  },

  setTaskHistory: (history: TaskHistoryItem[], total: number) => {
    set(state => {
      state.taskHistory = history;
      state.totalHistoryCount = total;
    });
  },

  setTaskStats: (stats: TaskStats) => {
    set(state => {
      state.taskStats = stats;
      state.taskUsage = stats.usage; // Also update usage from stats
    });
  },

  resetTaskData: () => {
    set(state => {
      state.taskUsage = null;
      state.taskHistory = [];
      state.totalHistoryCount = 0;
      state.taskStats = null;
      state.hasUsageError = false;
      state.hasHistoryError = false;
      state.hasStatsError = false;
    });
  },

  /* Methods */
  fetchTaskUsage: async (brandId: number) => {
    if (!brandId) {
      return;
    }

    set({ isLoadingUsage: true, hasUsageError: false });

    try {
      const usage = await getTaskUsage_api(brandId);
      const { isGrandfathered } = useBrand.getState();

      if (!isGrandfathered && usage) {
        // For new, non-grandfathered users, ensure they start with 1 task credit.
        // This might override what the API returns if the API isn't yet updated
        // or serves as a safeguard.
        // We assume the API might return 0 remaining for a truly new brand.
        if (usage.monthlyLimit === 0 && usage.used === 0) { // A heuristic for "truly new" or free tier equivalent
            usage.remaining = 1;
            // Potentially, we might also want to set usage.monthlyLimit = 1 if that's how it's displayed
            // For now, just ensuring 'remaining' is 1.
        } else if (usage.monthlyLimit > 0 && usage.remaining === 0 && usage.used === 0) {
            // Another case: if they have a limit > 0 (e.g. from a previous free tier that had some limit)
            // but used 0 and have 0 remaining, give them the initial credit.
            // This is less likely if the API correctly initializes new users to a state where they'd get 1 credit.
            usage.remaining = 1;
        }
        // If the API already correctly gives them 1 credit (e.g. usage.remaining = 1 from API), this won't wrongly deduct.
      }

      set(state => {
        state.taskUsage = usage;
        state.hasUsageError = false;
      });
    } catch (error) {
      Logger.error('Error fetching task usage:', error);
      set({ hasUsageError: true });
    } finally {
      set({ isLoadingUsage: false });
    }
  },

  fetchTaskHistory: async (brandId: number, options = {}) => {
    if (!brandId) {
      return;
    }

    set({ isLoadingHistory: true, hasHistoryError: false });

    try {
      const history = await getTaskHistory_api(brandId, options);
      set(state => {
        state.taskHistory = history.tasks;
        state.totalHistoryCount = history.total;
        state.hasHistoryError = false;
      });
    } catch (error) {
      Logger.error('Error fetching task history:', error);
      set({ hasHistoryError: true });
    } finally {
      set({ isLoadingHistory: false });
    }
  },

  fetchTaskStats: async (brandId: number) => {
    if (!brandId) {
      return;
    }

    set({ isLoadingStats: true, hasStatsError: false });

    try {
      const stats = await getTaskStats_api(brandId);
      set(state => {
        state.taskStats = stats;
        state.taskUsage = stats.usage; // Also update usage from stats
        state.hasStatsError = false;
      });
    } catch (error) {
      Logger.error('Error fetching task stats:', error);
      set({ hasStatsError: true });
    } finally {
      set({ isLoadingStats: false });
    }
  },

  checkTaskAvailability: async (brandId: number, taskType: TaskType): Promise<TaskAvailability> => {
    if (!brandId) {
      throw new Error('Brand ID is required');
    }

    try {
      const availability = await checkTaskAvailability_api(brandId, taskType);
      return availability;
    } catch (error) {
      Logger.error('Error checking task availability:', error);
      throw error;
    }
  },

  fetchAllTaskLimits: async () => {
    set({ isLoadingLimits: true, hasLimitsError: false });

    try {
      const limits = await getAllTaskLimits_api();
      set(state => {
        state.taskLimits = limits;
        state.hasLimitsError = false;
      });
    } catch (error) {
      Logger.error('Error fetching task limits:', error);
      set({ hasLimitsError: true });
    } finally {
      set({ isLoadingLimits: false });
    }
  },

  fetchRecommendedTasks: async (brandId: number) => {
    if (!brandId) {
      return;
    }

    set({ isLoadingRecommendedTasks: true, hasRecommendedTasksError: false });

    try {
      const response = await getRecommendedTasks_api(brandId);
      set(state => {
        state.recommendedTasks = response;
      });
    } catch (error) {
      Logger.error('Error fetching recommended tasks:', error);
      set(state => {
        state.hasRecommendedTasksError = true;
      });
    } finally {
      set({ isLoadingRecommendedTasks: false });
    }
  },

  refreshTaskData: async (brandId: number) => {
    if (!brandId) {
      return;
    }

    try {
      await Promise.all([
        get().fetchRecommendedTasks(brandId)
      ]);
    } catch (error) {
      Logger.error('Error refreshing task data:', error);
    }
  }
})));

export default useMaestroTasks; 